import { Checkbox, ConfigProvider } from 'antd';
import Modal from 'components/Modal';
import colorTheme from 'constants/color';
import { APPOINTMENT_STATUS } from 'features/appointment/services/constants';
import { IBodyApiUpdateStatusMultipleAppointment } from 'features/appointment/services/types/api';
import { AptDetailV3Data } from 'features/appointment/services/types/appointment';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import AppointmentSelectCard from '../sub-appointment-shared/appointment-select-card';
import CustomerSummary from '../sub-appointment-shared/customer-summary';
import { buildStatusBody } from '../sub-appointment-shared/helpers';

type IModalConfirmSubAppointmentProps = {};
type IModalConfirmSubAppointmentRef = {
  open: (detail: AptDetailV3Data, status?: number) => Promise<IBodyApiUpdateStatusMultipleAppointment>;
  close: () => void;
};
export const useModalConfirmSubAppointment = () => useRef<IModalConfirmSubAppointmentRef>(null);

const ModalConfirmSubAppointment = forwardRef<IModalConfirmSubAppointmentRef, IModalConfirmSubAppointmentProps>((_props, ref) => {
  const [visible, setVisible] = useState(false);
  const [detail, setDetail] = useState<AptDetailV3Data | null>(null);
  const [statusToApply, setStatusToApply] = useState<number>(APPOINTMENT_STATUS.CONFIRM);
  const [selectedKeys, setSelectedKeys] = useState<Set<number>>(new Set());
  const callbackResolve = useRef<(data: IBodyApiUpdateStatusMultipleAppointment) => void>(() => undefined);

  const items = detail?.items ?? [];
  const allChecked = items.length > 0 && selectedKeys.size === items.length;
  const indeterminate = selectedKeys.size > 0 && !allChecked;

  const resolveWith = (body: IBodyApiUpdateStatusMultipleAppointment) => {
    callbackResolve.current(body);
    callbackResolve.current = () => undefined;
  };

  const handleClose: IModalConfirmSubAppointmentRef['close'] = () => {
    resolveWith({ appointmentIds: [], status: statusToApply, reason: '', createdBy: '' });
    setVisible(false);
  };

  const handleOk = () => {
    if (!detail || selectedKeys.size === 0) return;
    resolveWith(buildStatusBody(detail.items, selectedKeys, statusToApply, ''));
    setVisible(false);
  };

  const toggleKey = (index: number) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedKeys((prev) => (prev.size === items.length ? new Set() : new Set(items.map((_, i) => i))));
  };

  const handleOpen: IModalConfirmSubAppointmentRef['open'] = (nextDetail, status = APPOINTMENT_STATUS.CONFIRM) => {
    setDetail(nextDetail);
    setStatusToApply(status);
    setSelectedKeys(new Set(nextDetail.items.map((_, i) => i)));
    setVisible(true);
    return new Promise((resolve) => {
      callbackResolve.current = resolve;
    });
  };

  useImperativeHandle(ref, () => ({ open: handleOpen, close: handleClose }));

  const modalTitle = useMemo(() => {
    switch (statusToApply) {
      case APPOINTMENT_STATUS.CONFIRM:
        return 'Confirm appointment';
      case APPOINTMENT_STATUS.NO_SHOW:
        return 'No show appointment';
      case APPOINTMENT_STATUS.CANCEL:
        return 'Cancel appointment';
      default:
        return 'Appointment';
    }
  }, [statusToApply]);

  return (
    <Modal
      visible={visible}
      width={900}
      v2
      modalTitle={modalTitle}
      okTitle='OK'
      cancelTitle='Close'
      disableOk={selectedKeys.size === 0}
      onClose={handleClose}
      onSubmit={handleOk}
      noneBodyStyle
      noneFooterStyle
      containerPadding={0}
    >
      <Body>
        <div className='summary-card'>
          <CustomerSummary centered name={detail?.customerName} phone={detail?.customerPhone} date={detail?.startTime} />
        </div>

        <div className='booking'>
          <ConfigProvider theme={{ components: { Checkbox: { colorPrimary: colorTheme.v2_secondary_3, colorPrimaryHover: colorTheme.v2_secondary_3, borderRadiusSM: 3, controlInteractiveSize: 32 } } }}>
            <Checkbox className='select-all' checked={allChecked} indeterminate={indeterminate} onChange={toggleAll}>
              <span className='select-all-label'>Select all</span>
            </Checkbox>
          </ConfigProvider>

          <div className='grid'>
            {items.map((item, index) => (
              <AppointmentSelectCard
                key={item.detailAppointmentId || index}
                item={item}
                checked={selectedKeys.has(index)}
                onToggle={() => toggleKey(index)}
              />
            ))}
          </div>
        </div>
      </Body>
    </Modal>
  );
});

ModalConfirmSubAppointment.displayName = 'ModalConfirmSubAppointment';

export default ModalConfirmSubAppointment;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 32px;

  .summary-card {
    display: flex;
    align-items: stretch;
    gap: 24px;
    padding: 16px;
    border: 1px solid ${colorTheme.v2_neutral_3};
    border-radius: 16px;
  }

  .booking {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .select-all .select-all-label {
    color: ${colorTheme.v2_text_5};
    font-family: Poppins;
    font-size: 18px;
    font-weight: 500;
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
`;
